/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import React, {
  ChangeEvent, FormEvent, useEffect, useState
} from 'react';
import {
  IlButton, IlInlineLoading, IlTextInput, IlInlineNotification, IlLoading
} from 'il-framework-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { useLocation } from 'react-router';
import { I18nNamespaces, LOA_RESEND_SECONDS } from '../../../../app/shared/utils/constants.util';
import { RootState } from '../../../../app/store';
import { SecuritySetupType } from '../../../../domain/models/impl/SecuritySetup';
import { getLoaValidationMessageKey, maskEmailFixed, useRequiredLevel } from '../../../../app/shared/utils/helpers.util';
import usePreserveSearchNav from '../../../../app/shared/hooks/usePreserveSearchNav';
import {
  setError,
  setValidationError,
  verifyOrActivateFactor,
  challengeFactor,
  loadSecuritySetupPage,
  selectFactorForVerify,
  setSelectedFactor
} from '../../setupSecuritySlice';

import { SecuritySetupRoutes } from '../../../../app/shared/utils/securitySetupRoutes';
import styles from './VerifyEmail.scss';

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const requiredLevel = useRequiredLevel();
  const { goBackOrReplace } = usePreserveSearchNav();
  const { search } = useLocation();
  const factorIdFromUrl = new URLSearchParams(search || '').get('factorId') ?? '';

  const actionLoading = useSelector((s: RootState) => s.setupSecurity.actionLoading);
  const validationError = useSelector((s: RootState) => s.setupSecurity.validationError);
  const authErrorKey = useSelector((s: RootState) => s.setupSecurity.error);
  const selectedFactor = useSelector((s: RootState) => s.setupSecurity.selectedFactor);
  const pageLoading = useSelector((s: RootState) => s.setupSecurity.pageLoading);
  const profileDetails = useSelector((s: RootState) => s.profile.profileDetails);
  const factorsLoaded = useSelector((s: RootState) => s.setupSecurity.factorsLoaded);
  const userEmail = profileDetails?.id ? profileDetails.externalId : '';

  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState<number>(0);

  const factorId = selectedFactor?.factorId ?? '';
  const digits = selectedFactor?.digits ?? 6;
  const canVerify = Boolean(factorId) && digits > 0;
  const typedDigits = code.replace(/\D/g, '').length;
  const isEmailFactor = selectedFactor?.type === SecuritySetupType.EMAIL;

  const isInitLoading = actionLoading && (!isEmailFactor || !factorId);

  const canSubmit = canVerify
    && !actionLoading
    && !validationError
    && typedDigits === digits;

  const shouldShowInlineNotification = !!authErrorKey;

  const canResend = Boolean(factorId) && resendIn === 0 && !actionLoading;

  useEffect(() => () => {
    dispatch(setError(''));
    dispatch(setValidationError(''));
    dispatch(setSelectedFactor(undefined));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!factorIdFromUrl) {
        goBackOrReplace(SecuritySetupRoutes.root);
        return;
      }

      if (!factorsLoaded) {
        await dispatch(loadSecuritySetupPage(t) as any);
        if (cancelled) return;
      }

      if (selectedFactor?.factorId !== factorIdFromUrl) {
        dispatch(selectFactorForVerify(factorIdFromUrl));
      }
    };

    bootstrap();

    return () => { cancelled = true; };
  }, [dispatch, factorIdFromUrl, factorsLoaded, t, goBackOrReplace, selectedFactor?.factorId]);

  const sendCode = async () => {
    if (!factorId) return;
    if (selectedFactor?.type !== SecuritySetupType.EMAIL) return;

    const shouldStartCooldown = await dispatch(challengeFactor(
      t,
      {
        factorId,
        factorType: SecuritySetupType.EMAIL,
        toastKey: 'loaEnrichment.common.errors.sendFailedToast'
      }
    ) as any);
    if (shouldStartCooldown) {
      setResendIn(LOA_RESEND_SECONDS);
    }
  };

  const challengedOnceRef = React.useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (!selectedFactor) return;

    if (selectedFactor.type !== SecuritySetupType.EMAIL) {
      goBackOrReplace(SecuritySetupRoutes.root);
      return;
    }

    if (!factorId) return;
    if (challengedOnceRef.current[factorId]) return;
    challengedOnceRef.current[factorId] = true;

    sendCode();
  }, [selectedFactor, factorId, goBackOrReplace]);

  useEffect(() => {
    if (resendIn <= 0) {
      return undefined;
    };

    const id = window.setInterval(() => {
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);

    return () => window.clearInterval(id);
  }, [resendIn]);

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setError(''));
    if (validationError) dispatch(setValidationError(''));

    const input = e.currentTarget.value.replace(/\D/g, '');
    setCode(input.slice(0, digits));
  };

  const submit = (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    if (!factorId) return;

    dispatch(verifyOrActivateFactor(t, {
      factorId,
      code: code.replace(/\s/g, ''),
      action: 'verify',
      successToastMsg: 'loaEnrichment.common.messages.verified',
      elevateLevel: selectedFactor?.loaLevel ?? requiredLevel
    }));
  };

  if (isInitLoading) {
    return <IlLoading withOverlay />;
  }

  return (
    pageLoading ? <IlLoading withOverlay /> : (
      <>
        <h1 className={styles.formTitle}>
          {t('loaEnrichment.common.titles.verifyIdentity')}
        </h1>
        <p className={styles.helpText}>
          {t('loaEnrichment.factors.email.verifyHelpText', { email: maskEmailFixed(userEmail) })}
        </p>
        {shouldShowInlineNotification && (
          <div className={styles.inlineNotification}>
            <IlInlineNotification
              title={t('loaEnrichment.common.errors.challengeFailedInline', { ns: I18nNamespaces.LOA_ENRICHMENT })}
              notificationType='inline'
              kind='error'
              hideCloseButton
            />
          </div>
        )}

        <form onSubmit={submit}>
          <IlTextInput
            id='email-verification-code'
            labelText={t('loaEnrichment.common.labels.verificationCode')}
            placeholder={t('loaEnrichment.common.placeholders.code', { digits })}
            type='text'
            maxLength={digits}
            invalid={!!validationError}
            invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
            value={code}
            onChange={updateCode}
            autoFocus
            autoComplete='off'
          />

          <div className={styles.activationContainer}>
            <div className={styles.resendContainer}>
              <p className={styles.resendText}>{t('loaEnrichment.common.resend.emailPrompt')}</p>
              <IlButton
                className={styles.resendBtn}
                kind='ghost'
                type='button'
                disabled={!canResend}
                onClick={sendCode}
              >
                {resendIn > 0
                  ? t('loaEnrichment.common.resend.resendIn', { seconds: resendIn })
                  : t('loaEnrichment.common.buttons.resendCode')}
              </IlButton>
            </div>

            <IlButton
              kind='primary'
              type='submit'
              disabled={!canSubmit}
            >
              {t('loaEnrichment.common.buttons.verify')}
            </IlButton>
          </div>
        </form>

        {actionLoading && (
          <div className={styles.loadingContainer}>
            <IlInlineLoading
              description={t('loaEnrichment.common.status.pleaseWait')}
              success={false}
              onSuccess={noop}
            />
          </div>
        )}
      </>
    )
  );
};

export default VerifyEmail;
